import type { FieldErrors, UseFormRegister } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Button, TextInput, Typography } from "@/components/ui";
import useScreens from "@/hooks/useScreens";
import { cn } from "@/lib/utils";
import useSidebarStore from "@/store/useSidebarStore";

type JobNameSchema = {
  job_name?: string | undefined;
};

type CreationMode = "standard" | "ai" | "pro";

interface JobHeaderProps {
  isPositionFixed: boolean;
  creationMode: CreationMode;
  register: UseFormRegister<JobNameSchema>;
  errors: FieldErrors<JobNameSchema>;
  atLeastOneSectionFilled: boolean;
  areAllRequiredFieldsFilled: boolean;
  isPending: boolean;
  onSaveAsDraft: () => void;
  onRunCalculations: () => void;
  canRunCalculations?: boolean;
}

export function JobHeader({
  isPositionFixed,
  creationMode,
  register,
  errors,
  atLeastOneSectionFilled,
  areAllRequiredFieldsFilled,
  isPending,
  onSaveAsDraft,
  onRunCalculations,
  canRunCalculations = true,
}: JobHeaderProps) {
  const isTabletOrAbove = useScreens("md", "up");
  const navigate = useNavigate();
  const { isOpen: isSidebarOpen } = useSidebarStore();

  return (
    <div
      className={cn(
        "fixed z-30 flex w-full flex-col items-center border-b border-solid border-b-black bg-white pb-4 md:flex-row md:justify-between",
        isPositionFixed && isTabletOrAbove ? "fixed top-0 pt-4" : "static",
        isSidebarOpen && isPositionFixed && isTabletOrAbove && "w-[calc(100vw-350px)]",
        !isSidebarOpen && isPositionFixed && isTabletOrAbove && "w-[calc(100vw-140px)]"
      )}
    >
      <div className={cn(!isTabletOrAbove && "relative mb-4 flex w-full flex-1")}>
        {!isTabletOrAbove && (
          <button className="left-1 cursor-pointer" onClick={() => navigate("/jobs")}>
            <Typography className="text-primary-500" variant="text-md" weight="medium">
              Back
            </Typography>
          </button>
        )}
        <Typography
          variant={isTabletOrAbove ? "display-lg" : "text-md"}
          weight="semibold"
          className={cn(!isTabletOrAbove && "ml-20")}
        >
          Create new job
        </Typography>
      </div>
      <div
        className={cn(
          "mb-4 flex w-full flex-col items-center gap-2 md:mb-0 md:w-auto md:flex-row md:items-end md:gap-4",
          !isTabletOrAbove && "px-4"
        )}
      >
        {creationMode === "standard" && (
          <TextInput
            className="w-full md:w-fit"
            label="Plant Repair Identifier"
            placeholder="Plant Repair Identifier"
            {...register("job_name")}
            error={errors.job_name?.message}
          />
        )}
        {creationMode === "standard" && isTabletOrAbove && (
          <div className="flex flex-wrap gap-4">
            <Button
              disabled={!atLeastOneSectionFilled || !!errors.job_name?.message || isPending}
              onClick={onSaveAsDraft}
              size="lg"
            >
              Save as draft
            </Button>
            {canRunCalculations && (
              <Button
                disabled={!areAllRequiredFieldsFilled || !!errors.job_name?.message || isPending}
                onClick={onRunCalculations}
                size="lg"
              >
                Run calculations
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
